import { Star, Quote } from 'lucide-react';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function TestimonialsPage() {
  const testimonials = [
    {
      name: 'Sarah Chen',
      role: 'Operations Manager',
      company: 'Tech Solutions Inc.',
      location: 'Toronto',
      rating: 5,
      text: 'Syncline Logistics has been our go-to courier service for over 2 years. Their reliability and professionalism are unmatched. Same-day delivery works perfectly for our urgent client needs, and the tracking system gives us complete visibility.',
      initials: 'SC',
      date: 'January 2026'
    },
    {
      name: 'Michael Roberts',
      role: 'Supply Chain Director',
      company: 'Manufacturing Pro',
      location: 'Mississauga',
      rating: 5,
      text: 'We switched to Syncline for our LTL shipments and couldn\'t be happier. Their tracking system is excellent, and the team is always responsive. The cost savings compared to our previous provider have been significant while maintaining quality.',
      initials: 'MR',
      date: 'December 2025'
    },
    {
      name: 'Emily Thompson',
      role: 'Business Owner',
      company: 'Ontario Supplies Co.',
      location: 'Brampton',
      rating: 5,
      text: 'Outstanding service from start to finish. The drivers are professional, the rates are competitive, and deliveries are always on time. Syncline has made our logistics so much easier to manage. Highly recommend for any business in the GTA.',
      initials: 'ET',
      date: 'November 2025'
    },
    {
      name: 'David Kumar',
      role: 'Logistics Coordinator',
      company: 'E-Commerce Hub',
      location: 'Markham',
      rating: 5,
      text: 'Best courier service we\'ve used in Ontario. The real-time tracking gives us and our customers peace of mind. Their team goes above and beyond to ensure everything arrives safely and on schedule. The customer support is exceptional.',
      initials: 'DK',
      date: 'October 2025'
    },
    {
      name: 'Jennifer Martinez',
      role: 'Operations Lead',
      company: 'Fresh Foods Distribution',
      location: 'Oakville',
      rating: 5,
      text: 'Time-sensitive deliveries are crucial for our business, and Syncline never disappoints. Their same-day service is reliable, and the drivers understand the urgency. We\'ve had zero issues with damaged goods. Excellent service!',
      initials: 'JM',
      date: 'September 2025'
    },
    {
      name: 'Robert Anderson',
      role: 'Warehouse Manager',
      company: 'Industrial Parts Ltd.',
      location: 'Burlington',
      rating: 5,
      text: 'After trying several logistics companies, Syncline stands out for their professionalism and efficiency. They handle our heavy freight shipments with care, and scheduling is always flexible. Great communication throughout the entire process.',
      initials: 'RA',
      date: 'August 2025'
    }
  ];

  const stats = [
    { value: '500+', label: 'Happy Clients' },
    { value: '50K+', label: 'Deliveries Completed' },
    { value: '4.9/5', label: 'Average Rating' },
    { value: '99.5%', label: 'On-Time Rate' }
  ];

  return (
    <Layout>
      {/* Hero Section with Image */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-hero z-10" />
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-15"
            style={{
              backgroundImage: 'url(https://images.pexels.com/photos/6868948/pexels-photo-6868948.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)'
            }}
          />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-primary/10 blur-[120px] rounded-full z-10" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center mb-16 animate-slide-up">
            <Badge variant="outline" className="mb-4 border-primary/50 text-primary bg-primary/10">
              Testimonials
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              What Our <span className="text-gradient">Clients Say</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Don't just take our word for it. Here's what businesses across GTA and Ontario say about working with Syncline Logistics. Real feedback from real clients who trust us with their deliveries.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="pb-16 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <Card className="scroll-animate p-8 md:p-12 bg-gradient-card backdrop-blur-sm border-primary/20">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {stats.map((stat, index) => (
                <div key={index}>
                  <div className="text-4xl md:text-5xl font-bold text-gradient mb-2">{stat.value}</div>
                  <div className="text-muted-foreground">{stat.label}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="pb-24 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card 
                key={index} 
                className="scroll-animate p-6 md:p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-glow group flex flex-col"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Quote icon */}
                <div className="mb-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Quote className="h-6 w-6 text-primary" />
                  </div>
                </div>
                
                {/* Rating */}
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                  ))}
                </div>
                
                {/* Testimonial text */}
                <p className="text-muted-foreground leading-relaxed mb-6 flex-grow">
                  "{testimonial.text}"
                </p>
                
                {/* Author info */}
                <div className="pt-6 border-t border-border/50 mt-auto">
                  <div className="flex items-center gap-4">
                    <Avatar className="h-12 w-12 bg-gradient-primary">
                      <AvatarFallback className="bg-gradient-primary text-primary-foreground font-semibold">
                        {testimonial.initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="font-semibold">{testimonial.name}</div>
                      <div className="text-sm text-muted-foreground">{testimonial.role}</div>
                      <div className="text-sm text-muted-foreground">{testimonial.company} • {testimonial.location}</div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {testimonial.date}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="pb-24 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <Card className="scroll-animate p-12 bg-gradient-card backdrop-blur-sm border-primary/20 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Ready to Join Our <span className="text-gradient">Happy Clients?</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Experience the same reliable service that our clients rave about. Get started with a free quote today.
            </p>
            <Link to="/quote">
              <Button size="lg" className="bg-gradient-primary hover:shadow-glow-lg transition-all duration-300 text-lg px-8">
                Get Your Free Quote
              </Button>
            </Link>
          </Card>
        </div>
      </section>
    </Layout>
  );
}
